import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Search,
  Mail,
  User,
  Building,
  Calendar,
  Star,
  Eye,
  CheckCircle,
  Linkedin,
  MapPin,
  DollarSign
} from 'lucide-react';
import { Button } from './components';

// No outreach data available yet - these will be populated from API when outreach module is implemented
const outreachData = [];

function OutreachPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [sortField, setSortField] = useState('lastContact');
  const [sortDirection, setSortDirection] = useState('desc');
  const itemsPerPage = 10;

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = outreachData.filter(contact => {
      const matchesSearch = 
        contact.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.notes.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = typeFilter === 'all' || contact.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || contact.priority === priorityFilter;
      const matchesAssignedTo = assignedToFilter === 'all' || contact.assignedTo === assignedToFilter;
      
      const matchesTab = activeTab === 'all' || contact.type === activeTab;
      
      return matchesSearch && matchesType && matchesStatus && matchesPriority && matchesAssignedTo && matchesTab;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'potentialRevenue') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (sortField === 'lastContact' || sortField === 'nextFollowUp') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [searchTerm, typeFilter, statusFilter, priorityFilter, assignedToFilter, activeTab, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredAndSortedData.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    if (status === 'urgent') {
      return 'bg-red-800 text-white border border-red-900';
    }
    return 'bg-gray-100 text-gray-800 border border-gray-300';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'urgent') {
      return 'bg-red-800 text-white border border-red-900';
    }
    return 'bg-gray-100 text-gray-700 border border-gray-300';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'senior_facility': return <Building className="w-3 h-3" />;
      case 'wedding_planner': return <Calendar className="w-3 h-3" />;
      case 'corporate': return <User className="w-3 h-3" />;
      case 'hotel': return <Building className="w-3 h-3" />;
      default: return <User className="w-3 h-3" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'senior_facility': return 'Senior Facility';
      case 'wedding_planner': return 'Wedding Planner';
      case 'corporate': return 'Corporate';
      case 'hotel': return 'Hotel/Resort';
      default: return type;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not contacted';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate statistics
  const totalContacts = outreachData.length;
  const totalPotentialRevenue = outreachData.reduce((sum, contact) => sum + contact.potentialRevenue, 0);
  const highPriorityContacts = outreachData.filter(c => c.priority === 'high').length;
  const qualifiedLeads = outreachData.filter(c => c.status === 'qualified' || c.status === 'proposal_sent').length;

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Page Title */}
      <div className="mb-2">
        <h1 className="text-lg font-semibold text-gray-900">OUTREACH</h1>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Contacts</p>
              <p className="text-lg font-semibold text-gray-800">{totalContacts}</p>
            </div>
            <User className="w-8 h-8 text-red-800" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Potential Revenue</p>
              <p className="text-lg font-semibold text-gray-800">{formatCurrency(totalPotentialRevenue)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-red-800" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">High Priority</p>
              <p className="text-lg font-semibold text-red-600">{highPriorityContacts}</p>
            </div>
            <Star className="w-8 h-8 text-red-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Qualified Leads</p>
              <p className="text-lg font-semibold text-green-600">{qualifiedLeads}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-3">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-3">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'all'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Contacts
            </button>
            <button
              onClick={() => setActiveTab('senior_facility')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'senior_facility'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Senior Facilities
            </button>
            <button
              onClick={() => setActiveTab('wedding_planner')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'wedding_planner'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Wedding Planners
            </button>
            <button
              onClick={() => setActiveTab('corporate')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'corporate'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Corporate
            </button>
            <button
              onClick={() => setActiveTab('hotel')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'hotel'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Hotels & Resorts
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Types</option>
              <option value="senior_facility">Senior Facilities</option>
              <option value="wedding_planner">Wedding Planners</option>
              <option value="corporate">Corporate</option>
              <option value="hotel">Hotels & Resorts</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="proposal_sent">Proposal Sent</option>
              <option value="meeting_scheduled">Meeting Scheduled</option>
              <option value="closed_won">Closed Won</option>
              <option value="closed_lost">Closed Lost</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={assignedToFilter}
              onChange={(e) => setAssignedToFilter(e.target.value)}
              className="w-full py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Assignees</option>
              <option value="Jade">Jade</option>
              <option value="Amber">Amber</option>
              <option value="Silvia">Silvia</option>
            </select>
          </div>

          {/* Contacts Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('company')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>COMPANY</span>
                      {sortField === 'company' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CONTACT</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LOCATION</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STATUS</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PRIORITY</th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('potentialRevenue')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>POTENTIAL REVENUE</span>
                      {sortField === 'potentialRevenue' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('lastContact')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>LAST CONTACT</span>
                      {sortField === 'lastContact' && (
                        sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentData.map((contact) => (
                  <tr key={contact.id} className="hover:bg-neutral-50">
                    <td className="px-2 py-1.5 text-xs">
                      <div className="flex items-center space-x-2">
                        {getTypeIcon(contact.type)}
                        <span className="text-gray-900">{getTypeLabel(contact.type)}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      <div>
                        <div className="font-medium text-gray-900">{contact.company}</div>
                        <div className="text-gray-500">{contact.contact} - {contact.title}</div>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-900">{contact.location}</span>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contact.status)}`}>
                        {contact.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(contact.priority)}`}>
                        {contact.priority}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-gray-900">{formatCurrency(contact.potentialRevenue)}</td>
                    <td className="px-2 py-1.5 text-xs text-gray-900">{formatDate(contact.lastContact)}</td>
                    <td className="px-2 py-1.5 text-xs">
                      <div className="flex space-x-2">
                        <button className="text-gray-400 hover:text-red-800 transition-colors">
                          <Eye className="w-3 h-3" />
                        </button>
                        <button className="text-gray-400 hover:text-red-800 transition-colors">
                          <Mail className="w-3 h-3" />
                        </button>
                        <button className="text-gray-400 hover:text-red-800 transition-colors">
                          <Linkedin className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-2 flex items-center justify-between">
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                size="sm"
                variant={currentPage === 1 ? 'secondary' : 'primary'}
              >
                Previous
              </Button>

              <div className="text-xs text-gray-600">
                Page {currentPage} of {totalPages}
              </div>

              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                size="sm"
                variant={currentPage === totalPages ? 'secondary' : 'primary'}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="pb-16" />
    </div>
  );
}

export default OutreachPage; 